import {
  defaultRange,
  isValidKey,
  orderRange,
  type CompareMode,
  type DayKey,
  type DayRange,
} from './dateRange';

export type StatsLevel = 'all' | 'campaigns' | 'automations';

/** What the Statistics report shows; lives in the URL so it survives reload and can be shared. */
export interface StatsQuery {
  range: DayRange;
  compare: CompareMode | 'off';
  /** Only for `compare === 'custom'`. */
  customCompare: DayRange | null;
  label: string | null;
  level: StatsLevel;
  /** Drilled-into automation id (only with level `automations`). */
  flow: string | null;
}

const COMPARE_VALUES = ['prev', 'month', 'custom', 'off'] as const;
const LEVELS: StatsLevel[] = ['all', 'campaigns', 'automations'];

/** A range from two params, or null when either is missing or not a real date. Future days are clamped to today. */
function readRange(from: string | null, to: string | null, today: DayKey): DayRange | null {
  if (!isValidKey(from) || !isValidKey(to)) return null;
  const r = orderRange(from, to);
  if (r.start > today) return null;
  return { start: r.start, end: r.end > today ? today : r.end };
}

export function parseStatsParams(params: URLSearchParams, today: DayKey): StatsQuery {
  const range = readRange(params.get('from'), params.get('to'), today) ?? defaultRange(today);
  const cmpRaw = params.get('cmp');
  let compare: StatsQuery['compare'] = (COMPARE_VALUES as readonly string[]).includes(cmpRaw ?? '')
    ? (cmpRaw as StatsQuery['compare'])
    : 'prev';
  const customCompare =
    compare === 'custom' ? readRange(params.get('cfrom'), params.get('cto'), today) : null;
  if (compare === 'custom' && !customCompare) compare = 'prev';
  const levelRaw = params.get('level') as StatsLevel | null;
  const level = levelRaw && LEVELS.includes(levelRaw) ? levelRaw : 'all';
  const flow = level === 'automations' ? params.get('flow') || null : null;
  return { range, compare, customCompare, label: params.get('label') || null, level, flow };
}

/** Writes the report state into `base` (other params, like `campaign`, are kept). */
export function writeStatsParams(query: StatsQuery, base: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const k of ['from', 'to', 'cmp', 'cfrom', 'cto', 'label', 'level', 'flow']) next.delete(k);
  next.set('from', query.range.start);
  next.set('to', query.range.end);
  next.set('cmp', query.compare);
  if (query.compare === 'custom' && query.customCompare) {
    next.set('cfrom', query.customCompare.start);
    next.set('cto', query.customCompare.end);
  }
  if (query.label) next.set('label', query.label);
  if (query.level !== 'all') next.set('level', query.level);
  if (query.level === 'automations' && query.flow) next.set('flow', query.flow);
  return next;
}
